import { useCallback, useEffect, useState } from 'react';

import { faStar as farStar } from '@fortawesome/free-regular-svg-icons';
import {
  faExclamationTriangle,
  faObjectGroup,
  faPlusCircle,
  faStar,
  faTimeline,
  faWarning,
  faEdit,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuthInfo } from '@propelauth/react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Tour from 'reactour';
import { toast } from 'sonner';

import AddToChronologyModalContent from './components/AddToChronologyModal';
import FactAdder from './components/FactAdder';
import FactFiltersPopup from './components/FactFilters';
import { OrgPermission } from '../../../../../../../../backend/src/organisation/organisation-permission';
import { useUserContext } from '../../../../../../Contexts/User';
import { ClusterFactsModal } from '../components/ClusterFactsModal';
import { CreateInconsistencyModal } from '../components/CreateInconsistencyModal';
import { useSelectedFactsDb } from '../hooks/useSelectedFactsDb';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useCreateChronology } from '@/api/mutations/useCreateChronology';
import { useGetCaseThemes } from '@/api/queries/useGetCaseThemes';
import { useGetChronologies } from '@/api/queries/useGetChronologies';
import { useGetDocIds } from '@/api/queries/useGetDocIds';
import { Person, Run } from '@/api/types/backend-dto-types';
import Button from '@/components/atoms/Button';
import Dropdown from '@/components/molecules/Dropdown';
import ExpandableButton from '@/components/molecules/ExpandableButton';
import { FilterExpandableButton } from '@/components/molecules/FilterExpandableButton';
import CustomModal from '@/components/molecules/Modals/CustomModal';
import { FactBulkUpdateModal } from '@/components/molecules/Modals/FactBulkUpdateModal';
import SettingsModal from '@/components/molecules/Modals/SettingsModal';
import NaturalLanguageSearchBox from '@/components/molecules/NaturalLanguageSearchBox';
import SearchBox from '@/components/molecules/SearchBox';
import Pagination from '@/components/organisms/Pagination';
import { SEARCH_STEPS } from '@/constants/tours';
import { useOrgPermissions } from '@/hooks/orgPermissions';
import { addFactsBatchToVisualisation } from '@/screens/Chronos/CaseEditor/DataView/ChronologyViewer/ChronologyEditor/VisualTimeline/SyncingHelpers/syncVisualisationWithTimelineChanges';
import DataViewToolbar from '@/screens/Chronos/CaseEditor/DataView/components/TableToolbar';
import { DocIds, FactFilters } from '@/types';

interface FactsEditorToolbarProps {
  globalFilter: string;
  resultsIncludedLength: number;
  refetchCaseFacts: () => void;
  clearSearch: () => void;
  setGlobalFilter: (value: string) => void;
  canGetPrevPage: boolean;
  canGetNextPage: boolean;
  prevPage: () => void;
  nextPage: () => void;
  currentPage: number;
  noOfPages: number;
  onSearchCall: (searchValue?: string, useNaturalLanguage?: boolean) => void;
  goToPage: (value: number) => void;
  keyFactFilterActive: boolean;
  hasKeyFacts: boolean;
  loadingKeyFacts: boolean;
  handleKeyFactFilterToggle: () => void;
  disputedFactFilterActive: boolean;
  handleDisputedFactFilterToggle: () => void;
  activeFilters: FactFilters;
  setFilters: (v: FactFilters) => void;
  caseRuns: Run[];
  allDocumentTypes: string[];
  pageSize: number;
  onPageSizeChange: (pageSize: number) => void;
  allPersons: Person[];
  onClusterComplete: () => void;
  processingNL?: boolean;
  onNLComplete: () => void;
}

const FactsEditorToolbar = ({
  globalFilter,
  refetchCaseFacts,
  clearSearch,
  setGlobalFilter,
  resultsIncludedLength,
  canGetPrevPage,
  canGetNextPage,
  prevPage,
  nextPage,
  currentPage,
  noOfPages,
  onSearchCall,
  goToPage,
  keyFactFilterActive,
  hasKeyFacts,
  loadingKeyFacts,
  handleKeyFactFilterToggle,
  disputedFactFilterActive,
  handleDisputedFactFilterToggle,
  activeFilters,
  setFilters,
  caseRuns,
  allDocumentTypes,
  pageSize,
  onPageSizeChange,
  allPersons,
  onClusterComplete,
  processingNL,
  onNLComplete,
}: FactsEditorToolbarProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const caseId = searchParams.get('caseId');

  const { data: themesWithCounts = [] } = useGetCaseThemes(caseId);
  const caseThemes = themesWithCounts.map((theme) => theme.name);
  // State
  const [factAdderModalIsOpen, setFactAdderModalIsOpen] = useState(false);
  const [factBulkUpdateModalIsOpen, setFactBulkUpdateModalIsOpen] = useState(false);
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [selectChronologyModalIsOpen, setSelectChronologyModalIsOpen] = useState(false);
  const [exploreTourOpen, setExploreTourOpen] = useState(false);
  const [activeFilterCount, setActiveFilterCount] = useState(0);
  const [clusterModalOpen, setClusterModalOpen] = useState(false);
  const [createInconsistencyModalOpen, setCreateInconsistencyModalOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const authInfo = useAuthInfo();

  const { isEnabled } = useOrgPermissions();
  const { user } = useUserContext();
  const isLabellingOrg = user?.organisation?.isLabellingOrg ?? false;

  // Data fetching
  const { data: docIdsResponse } = useGetDocIds(caseId);
  const { data: chronologiesResponse, isLoading: isLoadingChronologies } = useGetChronologies({
    caseId,
    paginated: false,
  });

  const { getAllSelectedIds } = useSelectedFactsDb();

  const { createChronology } = useCreateChronology({
    onSuccess: (data) => {
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('chronologyId', data.chronologyId);
      searchParams.set('page', '1');
      navigate('/app/chronos/case-editor/data/chronology/view?' + searchParams.toString(), { replace: true });
    },
    onError: (error) => {
      toast.error('Failed to create chronology');
    },
  });

  // Effects
  useEffect(() => {
    // Check if explore = true is in seach params
    if (searchParams.get('explore') === 'true') {
      setExploreTourOpen(true);

      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('explore');
      // Update the URL without the 'explore' parameter
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    let count = 0;
    Object.keys(activeFilters).forEach((key) => {
      const filterValues = activeFilters[key as keyof FactFilters];
      if (filterValues && Array.isArray(filterValues) && filterValues.length > 0) {
        count += 1;
      }
    });
    setActiveFilterCount(count);
  }, [activeFilters]);

  // Handlers
  const handleNewFactCreated = useCallback(() => {
    trackCustomEvent(TrackingEvents.AddFact, { caseId });

    refetchCaseFacts();
    setFactAdderModalIsOpen(false);
  }, [refetchCaseFacts, caseId]);

  const onDropdownClick = (option: string) => {
    if (option === 'new') {
      handleCreateChronology();
    } else {
      handleAddToExistingChronology();
    }
  };

  const handleCreateChronology = async () => {
    const factIds = await getAllSelectedIds(caseId!);
    await createChronology.mutateAsync({ caseId: caseId, chronologyId: null, type: null, factIds });
    trackCustomEvent(TrackingEvents.CreateFactFromFactsPage, { caseId });
  };

  const handleAddToExistingChronology = () => {
    setSelectChronologyModalIsOpen(true);
  };

  const handleSelectChronology = async (chronologyId: string) => {
    setSelectChronologyModalIsOpen(false);
    try {
      // Get selected fact IDs
      const factIds = await getAllSelectedIds(caseId!);

      // Add facts to the chronology
      const result = await createChronology.mutateAsync({ caseId: caseId, chronologyId, type: null, factIds });

      // If facts were added to an existing chronology, sync the visual timeline
      if (result.addedFacts && result.addedFacts.length > 0) {
        await addFactsBatchToVisualisation({
          caseId: caseId!,
          chronologyId,
          newFacts: result.addedFacts,
          getAccessToken: authInfo.tokens.getAccessToken,
        });
      }

      trackCustomEvent(TrackingEvents.AddToChronologyFromFactsPage, { caseId, chronologyId });
    } catch (error) {
      console.error('Error adding facts to chronology:', error);
      toast.error('Failed to add facts to chronology');
    }
  };

  const dropdownOptions = [
    { label: 'Create New', key: 'new' },
    { label: 'Add to Existing', key: 'existing' },
  ];

  const handleNLSearchExecuted = useCallback(
    (trigger: 'wand' | 'spacebar') => {
      if (trigger === 'wand') {
        trackCustomEvent(TrackingEvents.NaturalLanguageSearchFactsWand, { caseId });
      } else {
        trackCustomEvent(TrackingEvents.NaturalLanguageSearchFactsSpacebar, { caseId });
      }
    },
    [caseId],
  );

  return (
    <>
      <DataViewToolbar
        titleSection={<h1 className="text-lg font-semibold">Facts</h1>}
        actionButtons={
          <>
            <Dropdown
              id="chronology"
              placeholder="Chronology"
              buttonIcon={<FontAwesomeIcon icon={faTimeline} className="" />}
              onOptionClick={onDropdownClick}
              options={dropdownOptions}
              isDisabled={resultsIncludedLength === 0}
              isLoading={createChronology.isLoading}
              className="h-8 shadow-none"
            />
            {isLabellingOrg && (
              <>
                <Button
                  icon={<FontAwesomeIcon icon={faObjectGroup} className="pr-0 xl:pr-2" />}
                  text="Cluster"
                  onClick={() => setClusterModalOpen(true)}
                  type="secondary"
                  size="xs"
                  rounded="base"
                  hideTextSmallScreen
                  disabled={resultsIncludedLength < 2}
                  className="px-2 py-2 shadow"
                />
                <Button
                  icon={<FontAwesomeIcon icon={faExclamationTriangle} className="pr-0 xl:pr-2" />}
                  text="Create Inconsistency"
                  onClick={() => setCreateInconsistencyModalOpen(true)}
                  type="secondary"
                  size="xs"
                  rounded="base"
                  hideTextSmallScreen
                  disabled={resultsIncludedLength < 2}
                  className="px-2 py-2 shadow"
                />
              </>
            )}
            <ExpandableButton icon={faPlusCircle} onClick={() => setFactAdderModalIsOpen(true)} className="px-2 py-2">
              <p className="text-sm">Add Fact</p>
            </ExpandableButton>
            <ExpandableButton
              icon={faEdit}
              onClick={() => setFactBulkUpdateModalIsOpen(true)}
              className="px-2 py-2"
              isDisabled={resultsIncludedLength === 0}
            >
              <p className="text-sm">Edit Selected Facts</p>
            </ExpandableButton>
          </>
        }
        searchBar={
          <div id="facts-search-bar">
            <NaturalLanguageSearchBox
              value={globalFilter}
              onChange={setGlobalFilter}
              onSearchCall={onSearchCall}
              clearSearch={clearSearch}
              processingNL={processingNL}
              onNLComplete={onNLComplete}
              onNLSearchExecuted={handleNLSearchExecuted}
              entityType="facts"
            />
          </div>
        }
        filterButtons={
          <>
            {isEnabled(OrgPermission.Inconsistencies) && (
              <ExpandableButton
                icon={faWarning}
                isActive={disputedFactFilterActive}
                onClick={handleDisputedFactFilterToggle}
                className={` ${disputedFactFilterActive && 'border-red-300 bg-red-50 text-red-500'}`}
              >
                <p className="text-sm">Disputed Facts</p>
              </ExpandableButton>
            )}

            <ExpandableButton
              hidden={!hasKeyFacts}
              isLoading={loadingKeyFacts}
              isActive={keyFactFilterActive}
              icon={keyFactFilterActive ? faStar : farStar}
              onClick={handleKeyFactFilterToggle}
              className={`${keyFactFilterActive && 'border-yellow-300 bg-yellow-50 text-yellow-500'}`}
            >
              <p className="text-sm">Key Facts</p>
            </ExpandableButton>
            <div className="relative">
              <FilterExpandableButton filterCount={activeFilterCount} onClick={() => setFiltersModalOpen(true)} />
            </div>
          </>
        }
        pagination={
          <Pagination
            canGetPrevPage={canGetPrevPage}
            canGetNextPage={canGetNextPage}
            prevPage={prevPage}
            nextPage={nextPage}
            currentPage={currentPage}
            noOfPages={noOfPages}
            goToPage={goToPage}
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
          />
        }
      />

      <Tour
        startAt={0}
        steps={SEARCH_STEPS}
        isOpen={exploreTourOpen}
        onRequestClose={() => setExploreTourOpen(false)}
      />
      <CustomModal
        content={
          <FactFiltersPopup
            activeFilters={activeFilters}
            allDocumentTypes={allDocumentTypes}
            setFilters={setFilters}
            handleClose={() => setFiltersModalOpen(false)}
            caseRuns={caseRuns}
            caseThemes={caseThemes}
            allPersons={allPersons || []}
            goToPage={goToPage}
          />
        }
        isOpen={filtersModalOpen}
        maxWidth="42rem"
        handleClose={() => setFiltersModalOpen(false)}
      />
      <SettingsModal
        title={
          <div className="flex items-center gap-2 font-semibold text-blue-500">
            <FontAwesomeIcon icon={faTimeline} className="" />
            Select Existing Chronology
          </div>
        }
        content={
          <AddToChronologyModalContent
            isLoadingChronologies={isLoadingChronologies}
            chronologies={chronologiesResponse?.chronologies ?? []}
            handleSelectChronology={handleSelectChronology}
          />
        }
        isOpen={selectChronologyModalIsOpen}
        size="small"
        handleClose={() => setSelectChronologyModalIsOpen(false)}
      />
      <SettingsModal
        title={
          <div className="flex items-center gap-2 font-semibold text-blue-500">
            <FontAwesomeIcon icon={faPlusCircle} className="" />
            Add Fact
          </div>
        }
        content={
          <FactAdder
            caseId={caseId || ''}
            docs={docIdsResponse?.documents ?? []}
            handleNewFactCreated={handleNewFactCreated}
          />
        }
        isOpen={factAdderModalIsOpen}
        size="small"
        handleClose={() => setFactAdderModalIsOpen(false)}
      />
      {clusterModalOpen && (
        <ClusterFactsModal
          isOpen={clusterModalOpen}
          onClose={() => setClusterModalOpen(false)}
          caseId={caseId || ''}
          onSuccess={() => {
            setClusterModalOpen(false);
            onClusterComplete();
          }}
        />
      )}
      {createInconsistencyModalOpen && (
        <CreateInconsistencyModal
          isOpen={createInconsistencyModalOpen}
          onClose={() => setCreateInconsistencyModalOpen(false)}
          caseId={caseId || ''}
          onSuccess={() => {
            setCreateInconsistencyModalOpen(false);
            refetchCaseFacts();
          }}
        />
      )}
      {factBulkUpdateModalIsOpen && (
        <FactBulkUpdateModal
          selectedFactCount={resultsIncludedLength}
          isOpen={factBulkUpdateModalIsOpen}
          handleClose={() => setFactBulkUpdateModalIsOpen(false)}
          caseId={caseId || ''}
          onSuccess={() => {
            setFactBulkUpdateModalIsOpen(false);
          }}
        />
      )}
    </>
  );
};

export default FactsEditorToolbar;
